import { getSiteConfig } from './siteConfigService';
import { meetingService, Meeting } from './meetingService';

export const WORKSHOP_2026_PATH = '/2026-workshop';

const EASTERN_TZ = 'America/New_York';
const DEFAULT_WORKSHOP_TZ = 'America/Los_Angeles';

const IANA_ABBREVIATIONS: Record<string, string> = {
  'America/Los_Angeles': 'PT',
  'America/Denver': 'MT',
  'America/Chicago': 'CT',
  'America/New_York': 'ET',
  'America/Phoenix': 'MST',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
};

interface TimeParts {
  hour12: number;
  minute: number;
  ampm: 'AM' | 'PM';
}

export interface FormatWorkshopTimeOptions {
  /** Include end time from schedule duration (e.g. 7:15–8:15 AM). */
  includeEnd?: boolean;
}

export function meetingCodeIsWorkshop2026(
  meetingCode: string | null | undefined,
  siteWorkshopCode: string | null | undefined
): boolean {
  if (!meetingCode?.trim() || !siteWorkshopCode?.trim()) return false;
  return meetingCode.trim().toUpperCase() === siteWorkshopCode.trim().toUpperCase();
}

export interface Workshop2026Context {
  meeting: Meeting | null;
  meetingCode: string | null;
  loadError: string | null;
}

export async function loadWorkshop2026Context(options?: { authed?: boolean }): Promise<Workshop2026Context> {
  const site = await getSiteConfig();
  const code = (site.workshop_2026_meeting_code || '').trim();
  if (!code) {
    return { meeting: null, meetingCode: null, loadError: null };
  }
  try {
    const meeting = options?.authed
      ? await meetingService.getMeetingByCodeAuthed(code)
      : await meetingService.getMeetingByCode(code);
    return { meeting, meetingCode: code.toUpperCase(), loadError: null };
  } catch (e) {
    return {
      meeting: null,
      meetingCode: code.toUpperCase(),
      loadError: e instanceof Error ? e.message : 'Failed to load workshop',
    };
  }
}

export function formatWorkshopSchedule(meeting: Meeting | null): string {
  const day = pluralizeScheduleDay(meeting?.schedule_day || 'Saturday');
  return `${day}, ${formatWorkshopTimeDualTz(meeting)}`;
}

/** Recurring day + time range with Eastern — for FAQ time-commitment copy. */
export function formatWorkshopDayTimeRange(meeting: Meeting | null): string {
  const day = pluralizeScheduleDay(meeting?.schedule_day || 'Saturday');
  return `${day}, ${formatWorkshopTimeDualTz(meeting, { includeEnd: true })}`;
}

export function formatWorkshopTimeDualTz(
  meeting: Meeting | null,
  options: FormatWorkshopTimeOptions = {}
): string {
  const { includeEnd = false } = options;
  const meetingTz = meeting?.timezone || DEFAULT_WORKSHOP_TZ;
  const { hours, minutes } = parseScheduleTime(meeting?.schedule_start_time);
  const durationMin = getWorkshopDurationMinutes(meeting);
  const refDate = getScheduleReferenceDate(meeting);

  const startUtc = localWallTimeToUtc(refDate, hours, minutes, meetingTz);
  const endUtc = new Date(startUtc.getTime() + durationMin * 60 * 1000);
  const endForRange = includeEnd ? endUtc : null;

  const primaryAbbrev = getIanaAbbrev(meetingTz);
  const primary = formatZoneTimeSegment(startUtc, endForRange, meetingTz, primaryAbbrev);

  if (meetingTz === EASTERN_TZ) {
    return primary;
  }

  const eastern = formatZoneTimeSegment(startUtc, endForRange, EASTERN_TZ, 'ET');
  return `${primary} / ${eastern}`;
}

function pluralizeScheduleDay(day: string): string {
  const trimmed = day.trim();
  if (!trimmed) return 'Saturdays';
  const lower = trimmed.toLowerCase();
  if (lower.endsWith('s')) return trimmed;
  return `${trimmed}s`;
}

/** First session date + recurring time (for Zoom block and list-sharing copy). */
export function formatWorkshopStartSchedule(meeting: Meeting | null): string {
  const timePart = formatWorkshopTimeDualTz(meeting);
  const dateStr = meeting?.workshop_first_session_date;
  if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    try {
      const d = new Date(`${dateStr}T12:00:00`);
      const dayDate = d.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      return `${dayDate} · ${timePart}`;
    } catch {
      /* fall through */
    }
  }
  const day = meeting?.schedule_day || 'Saturday';
  return `${day}, ${timePart} (start date TBD)`;
}

function getWeekdayFromScheduleDay(day: string | undefined): number | null {
  const normalized = (day || 'Saturday').toLowerCase().replace(/s$/, '');
  const map: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return map[normalized] ?? null;
}

function parseSessionDate(dateStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatSessionDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getNextMeetingDate(meeting: Meeting | null, fromDate: Date): Date | null {
  const firstSession = parseSessionDate(meeting?.workshop_first_session_date || '');
  if (!firstSession) return null;

  const today = new Date(fromDate);
  today.setHours(0, 0, 0, 0);

  if (today < firstSession) return firstSession;

  const weekday = getWeekdayFromScheduleDay(meeting?.schedule_day);
  if (weekday === null) return firstSession;

  const next = new Date(today);
  const daysUntil = (weekday - next.getDay() + 7) % 7;
  next.setDate(next.getDate() + daysUntil);
  return next;
}

/** First or next meeting line for the Join live block. */
export function formatWorkshopMeetingLine(meeting: Meeting | null): string {
  const timePart = formatWorkshopTimeDualTz(meeting);

  const firstSession = parseSessionDate(meeting?.workshop_first_session_date || '');
  if (!firstSession) {
    const day = meeting?.schedule_day || 'Saturday';
    return `First meeting: ${day}, ${timePart} (date TBD)`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (today < firstSession) {
    return `First meeting: ${formatSessionDate(firstSession)} · ${timePart}`;
  }

  const nextMeeting = getNextMeetingDate(meeting, today) || firstSession;
  return `Next meeting: ${formatSessionDate(nextMeeting)} · ${timePart}`;
}

export function formatFirstSessionDate(meeting: Meeting | null): string {
  const dateStr = meeting?.workshop_first_session_date;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return 'date TBD';
  }
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'date TBD';
  }
}

function parseScheduleTime(startTime: string | undefined): { hours: number; minutes: number } {
  if (startTime && /^\d{1,2}:\d{2}$/.test(startTime)) {
    const [h, m] = startTime.split(':').map(Number);
    return { hours: h, minutes: m };
  }
  return { hours: 7, minutes: 15 };
}

function getWorkshopDurationMinutes(meeting: Meeting | null): number {
  const duration = meeting?.schedule_duration_minutes ?? meeting?.workshop_duration_minutes;
  return duration && duration > 0 ? duration : 60;
}

function getScheduleReferenceDate(meeting: Meeting | null): string {
  const dateStr = meeting?.workshop_first_session_date;
  if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  const today = new Date();
  const nextSaturday = new Date(today);
  const daysUntilSaturday = (6 - today.getDay() + 7) % 7 || 7;
  nextSaturday.setDate(today.getDate() + daysUntilSaturday);
  const y = nextSaturday.getFullYear();
  const mo = String(nextSaturday.getMonth() + 1).padStart(2, '0');
  const d = String(nextSaturday.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function localWallTimeToUtc(
  dateStr: string,
  hours: number,
  minutes: number,
  timeZone: string
): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  let utcMs = Date.UTC(year, month - 1, day, hours, minutes, 0);

  for (let attempt = 0; attempt < 5; attempt++) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(new Date(utcMs));

    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const actualHour = get('hour');
    const actualMinute = get('minute');
    const actualDay = get('day');

    if (actualHour === hours && actualMinute === minutes && actualDay === day) {
      return new Date(utcMs);
    }

    const diffMinutes = (hours - actualHour) * 60 + (minutes - actualMinute) + (day - actualDay) * 24 * 60;
    utcMs += diffMinutes * 60 * 1000;
  }

  return new Date(utcMs);
}

function getTimePartsInZone(utcDate: Date, timeZone: string): TimeParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(utcDate);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 12);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const ampm = (parts.find((p) => p.type === 'dayPeriod')?.value ?? 'AM').toUpperCase() as 'AM' | 'PM';
  return { hour12: hour, minute, ampm };
}

function formatTimeParts(parts: TimeParts): string {
  return `${parts.hour12}:${parts.minute.toString().padStart(2, '0')} ${parts.ampm}`;
}

function formatTimeRange(start: TimeParts, end: TimeParts): string {
  if (start.ampm === end.ampm) {
    return `${start.hour12}:${start.minute.toString().padStart(2, '0')}–${end.hour12}:${end.minute.toString().padStart(2, '0')} ${start.ampm}`;
  }
  return `${formatTimeParts(start)}–${formatTimeParts(end)}`;
}

function formatZoneTimeSegment(
  startUtc: Date,
  endUtc: Date | null,
  timeZone: string,
  abbrev: string
): string {
  const startParts = getTimePartsInZone(startUtc, timeZone);
  if (!endUtc) {
    return `${formatTimeParts(startParts)} ${abbrev}`;
  }
  const endParts = getTimePartsInZone(endUtc, timeZone);
  return `${formatTimeRange(startParts, endParts)} ${abbrev}`;
}

function getIanaAbbrev(iana: string): string {
  if (IANA_ABBREVIATIONS[iana]) {
    return IANA_ABBREVIATIONS[iana];
  }
  const last = iana.split('/').pop() || iana;
  const legacy: Record<string, string> = {
    Los_Angeles: 'PT',
    Denver: 'MT',
    Chicago: 'CT',
    New_York: 'ET',
  };
  return legacy[last] || last.replace('_', ' ');
}
